import { Injectable } from '@angular/core';
import { Subscription, switchMap, BehaviorSubject, Observable, forkJoin, of, map, filter } from 'rxjs';
import moment from 'moment-timezone';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { SavedMentionSearchesApi } from '../../../api/mentions/saved-mention-searches.api';
import { MentionSearchCriteriaModel } from '../models/mention-search-criteria.model';
import { MentionsApi } from '../../../api/mentions/mentions.api';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { MentionSearchFacetTypesEnum } from '../../../api/mentions/enum/mention-search-facet-types.enum';
import { NeoFacetTermFieldApiEnum } from '../../../api/mentions/enum/neo-facet-term-field-api.enum';
import { NeoFacetDateFieldApiEnum } from '../../../api/mentions/model/neo-facet-date-field-api.enum';
import { BucketResponseModel } from '../../../api/mentions/model/bucket-response.model';
import { DataPointModel } from '../../../Common/Charts/models/data-point.model';
import { SavedMentionSearchApiModel } from '../../../api/mentions/model/saved-mention-search.api.model';
import { MonitorConfigService } from '../../../Common/MonitorShared/services/monitor-config.service';
import { MentionSavingQuota } from '../../../api/monitoring/model/monitoring-account-configuration-api.model';
import { MentionsSearchResponseModel } from '../../../api/mentions/model/mentions-search-response.model';
import { FacetResponseModel } from '../../../api/mentions/model/facet-response.model';
import { number } from '@amcharts/amcharts4/core';

export interface QuotaUsageOverTimeSeries {
    id?: number;
    seriesType?: MentionSavingSeriesType;
    label: string;
    data: DataPointModel[];
}

export enum MentionSavingSeriesType {
    ALL = 'ALL',
    OTHER = 'OTHER',
}
@Injectable()
export class MentionSavingUsageDashboardService {
    private readonly sourceOverTimeKey: string = 'source_over_time';
    private readonly sourceTypeKey: string = 'source_type';
    private readonly sourceSearchKey: string = 'source_search';

    constructor(
        private localizationService: LocalizationService,
        private savedMentionSearchesApi: SavedMentionSearchesApi,
        private mentionsApi: MentionsApi,
    ){}

    quotaUsageFacetSearch(start: number, end: number, hasNeoFacet: boolean = true): Observable<MentionsSearchResponseModel>  {
        const mentionSearchCriteria = new MentionSearchCriteriaModel();
        mentionSearchCriteria.dateFacets = [];
        mentionSearchCriteria.facets = [
            MentionSearchFacetTypesEnum.saveSourceType,
            MentionSearchFacetTypesEnum.savingSearch,
        ];

        if (hasNeoFacet) {
            mentionSearchCriteria.neoFacets = [
                {
                    dateField: NeoFacetDateFieldApiEnum.TIMESTAMP,
                    key: this.sourceOverTimeKey,
                    plainBucketRequests: [
                        {
                            key: this.sourceTypeKey,
                            field: NeoFacetTermFieldApiEnum.SAVE_SOURCE_TYPE
                        },
                        {
                            key: this.sourceSearchKey,
                            field: NeoFacetTermFieldApiEnum.SAVING_SEARCH
                        }
                    ],
                    range: {
                        start: new Date(start).toISOString(),
                        end: new Date(end).toISOString()
                    },
                    gap: 86400000 // 1 day: 24hr * 60min * 60s * 1000ms
                }
            ];
        }

        const searchCriteria = mentionSearchCriteria.getApiEntity();
        searchCriteria.query.publicationDate = null;
        searchCriteria.query.savedDate = {
            dateType: DateQueryTypeEnum.ABSOLUTE,
            timeZone: 'UTC',
            dateRange: {start, end}
        };
        searchCriteria.pageSize = 0;

        return this.mentionsApi.searchAccount(searchCriteria);
    }

    getTopicsUsage(sampleRange: number): Observable<FacetResponseModel[]> {
        const end = moment().utc().endOf('day').valueOf();
        const start = moment().utc().subtract(sampleRange - 1, 'days').startOf('day').valueOf();
        return this.quotaUsageFacetSearch(start, end, false).pipe(
            map(results => results?.facetResults)
        );
    }

    private generateLineData(
        series: QuotaUsageOverTimeSeries[],
        buckets: BucketResponseModel[],
        topic?: SavedMentionSearchApiModel,
        seriesType?: MentionSavingSeriesType,
    ) {
        const lineData: DataPointModel[] = [{label: '0', count: 0, xValue: 0, yValue: 0}];

        for(let i = 0, yValue = 0; i < buckets.length; i++) {
            const xValue = moment.utc(buckets[i].intervalValue.start).date();

            const item = topic
                ? (buckets[i].buckets[this.sourceSearchKey] as BucketResponseModel[]).find(itm => itm.termValue === topic?.id.toString())
                : buckets[i];

            const count = item
                ? seriesType === MentionSavingSeriesType.OTHER
                    ? item.count - series.reduce((sum, sub) => sub.id ? +sub.data[i+1].count + sum : sum, 0)
                    : item.count
                : 0;

            yValue += count;

            lineData.push({
                label: xValue.toString(),
                xValue,
                yValue,
                count,
                rawDateFacetFrom: buckets[i].intervalValue.start,
                rawDateFacetTo: buckets[i].intervalValue.end
            });
        }

        const label = topic ? topic.name : this.localizationService.transform(`Monitoring.mentionSavingUsageDashboard.${seriesType}`);

        series.push({
            id: topic?.id,
            seriesType,
            label,
            data: lineData
        } as QuotaUsageOverTimeSeries);
    }

    getQuotaUsageOverTime(): Observable<QuotaUsageOverTimeSeries[]> {
        const start = moment.utc().startOf('month').valueOf();
        const end = moment.utc().endOf('day').valueOf();
        return this.quotaUsageFacetSearch(start, end).pipe(
            switchMap(results => {
                if(!results) {
                    return of(null);
                }

                const {facetResults, neoFacetResults} = results;
                const savingSearch = facetResults.find(
                    result => String(result.field) === String(NeoFacetTermFieldApiEnum.SAVING_SEARCH)
                );

                // Sorting ids by count
                const topSavingSearchIds = savingSearch?.values
                    ?.sort((a, b) => b.count - a.count)
                    ?.map(topic => +topic.value) || [];

                return forkJoin({
                    neoFacetResults: of(neoFacetResults),
                    topics: topSavingSearchIds.length ? this.savedMentionSearchesApi.getSavedSearchBulk(topSavingSearchIds).pipe(
                        map(topics => {
                            if(!topics) {
                                return [];
                            }
                            return Object.values(topics)
                                .sort((a, b) => topSavingSearchIds.indexOf(a.id) - topSavingSearchIds.indexOf(b.id))
                                .slice(0, 10); // Only the top 10 quota consumers are selected
                        })
                    ) : of(null)
                });
            }),
            switchMap(results => {
                const topics = results?.topics ?? [];
                const neoFacetResults = results?.neoFacetResults ?? [];
                const buckets = neoFacetResults?.[0]?.buckets ?? [];
                const quotaUsageOverTime: QuotaUsageOverTimeSeries[] = [];

                // Generate the All Usage line data
                this.generateLineData(quotaUsageOverTime, buckets, null, MentionSavingSeriesType.ALL);

                // Generate the top 10 auto saving search line date
                topics.forEach(topic => this.generateLineData(quotaUsageOverTime, buckets, topic));

                // Generate the other line date
                this.generateLineData(quotaUsageOverTime, buckets, null, MentionSavingSeriesType.OTHER);

                return of(quotaUsageOverTime);
            })
        );
    }
}
